import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { VehicleValidation, EvidenceFile } from '../../models/vehicle-validation';
import { VehicleValidationEditorService } from '../../services/vehicle-validation-editor.service';


@Component({
  selector: 'app-checklist-evidence-upload-view',
  templateUrl: './checklist-evidence-upload-view.component.html',
  styleUrls: ['./checklist-evidence-upload-view.component.scss', '../../styles/styles-component.scss']
})
export class ChecklistEvidenceUploadViewComponent implements OnInit, OnDestroy {
  @Output() evidencesUploaded = new EventEmitter<string[]>();

  activeVehicle: VehicleValidation | null = null;
  existingEvidences: string[] = [];
  newEvidenceFiles: EvidenceFile[] = [];
  deletingUrls = new Set<string>();
  dragging = false;
  uploading = false;

  private vehicleSubscription?: Subscription;

  constructor(
    public vehicleEditor: VehicleValidationEditorService,
    private sanitizer: DomSanitizer,
  ) { }

  ngOnInit(): void {
    this.newEvidenceFiles = [];
    this.vehicleSubscription = this.vehicleEditor.selectedVehicle$.subscribe(vehicle => {
      if(this.activeVehicle != null && this.activeVehicle.id != vehicle?.id){
        this.newEvidenceFiles = [];
      }
      this.activeVehicle = vehicle;
      if (vehicle) {
        const urls = [
          ...(vehicle.parameters?.evidences || [])
        ];
        this.existingEvidences = Array.from(new Set(urls));
      } else {
        this.existingEvidences = [];
      }
    });
  }

  ngOnDestroy(): void {
    if (this.vehicleSubscription) {
      this.vehicleSubscription.unsubscribe();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) return;

    this.processFiles(Array.from(input.files));

    input.value = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragging = false;

    if (!event.dataTransfer?.files?.length) return;

    this.processFiles(Array.from(event.dataTransfer.files));
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = false;
  }

  processFiles(files: File[]): void {
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) return;

      let preview = URL.createObjectURL(file);
      const safeUrl = this.sanitizer.bypassSecurityTrustUrl(preview);

      const evidence: EvidenceFile = {
        file,
        preview: safeUrl,
        progress: 0,
        uploaded: false,
        sizeLabel: this.formatBytes(file.size)
      };
      this.newEvidenceFiles.push(evidence);
    });
  }

  removeNewEvidence(index: number): void {
    this.newEvidenceFiles.splice(index, 1);
  }

  clearAll(): void {
    this.newEvidenceFiles = [];
  }

  async deleteExistingEvidence(url: string): Promise<void> {
    if (!this.activeVehicle) return;

    const result = await Swal.fire({
      title: '¿Eliminar evidencia?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    this.deletingUrls.add(url);
    try {
      const success = await this.vehicleEditor.removeEvidence(this.activeVehicle, url);
      if (success) {
        Swal.fire({
          title: 'Eliminado',
          text: 'La evidencia ha sido eliminada.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: 'No se pudo eliminar la evidencia.',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al eliminar la evidencia.',
        icon: 'error'
      });
    } finally {
      this.deletingUrls.delete(url);
    }
  }

  async uploadNewEvidences(): Promise<void> {
    if (!this.activeVehicle || this.newEvidenceFiles.length === 0) return;

    this.uploading = true;
    try {
      const newUrls = await this.vehicleEditor.uploadEvidences(this.activeVehicle, this.newEvidenceFiles);
      if (newUrls && newUrls.length > 0) {
        this.newEvidenceFiles = [];
        this.evidencesUploaded.emit(newUrls);
        this.onCancel();
        Swal.fire({
          title: 'Guardado',
          text: 'Las nuevas evidencias se subieron correctamente.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: 'No se pudo subir ninguna evidencia.',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al subir las evidencias.',
        icon: 'error'
      });
    } finally {
      this.uploading = false;
    }
  }

  viewImage(url: string): void {
    window.open(url, '_blank');
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onCancel() {
    this.vehicleEditor.closeEvidenceModal();
  }
}
